import _ from 'lodash';
import uuid from 'uuid/v4';
import { putDataPromise } from 'brock-fetch-utils';
import {
    predicateValues,
    printOrientation,
} from '../shared/constants/constants';
import {
    mergeTimeAndPrecision,
    mergeFilterValues,
    mergeWhereClauses,
} from './utils/metricRequestUtils';

const codec = require('json-url')('lzma');

const configKeyMap = [
    { original: 'predicate', transpiled: '-a' },
    { original: 'values', transpiled: '-b' },
    { original: 'startDateTime', transpiled: '-c' },
    { original: 'endDateTime', transpiled: '-d' },
    { original: 'precisionType', transpiled: '-e' },
    { original: 'precisionValue', transpiled: '-f' },
    { original: 'rangeType', transpiled: '-g' },
    { original: 'rangeValue', transpiled: '-h' },
    { original: 'timeAndPrecisionType', transpiled: '-i' },
    { original: 'timeAndPrecision', transpiled: '-j' },
    { original: 'filterValues', transpiled: '-k' },
    { original: 'whereClauses', transpiled: '-l' },
];

export const DASHBOARD_LINK = 'dashboardLink';
export const EXTERNAL_LINK = 'externalLink';
export const PERSISTED_LINK = 'persistedLink';
export const SAVED_DASHBOARD_NAME = 'savedName';
export const linkKeys = [
    PERSISTED_LINK,
    EXTERNAL_LINK,
    DASHBOARD_LINK,
    SAVED_DASHBOARD_NAME,
];

export function noLinksExist(queryParams) {
    const numberOfLinks = linkKeys.reduce(
        (total, link) => (queryParams[link] ? total + 1 : total),
        0
    );

    return numberOfLinks === 0;
}

export function multipleLinksExist(queryParams) {
    const numberOfLinks = linkKeys.reduce(
        (total, link) => (queryParams[link] ? total + 1 : total),
        0
    );

    return numberOfLinks > 1;
}

function buildTimeAndPrecisionArgument(timeAndPrecision = {}) {
    return _.pickBy(timeAndPrecision, (value) => value);
}

function buildFilterValueArgument(filterValues, dashboardKey) {
    const filterValuesValue = filterValues ?? {};
    const filter = { ...filterValuesValue[dashboardKey] };

    if (_.isEmpty(filter.values) || !filter.predicate) {
        console.log(`Filter has no values: ${dashboardKey}`); // eslint-disable-line no-console
        return null;
    }

    return filter;
}

function buildWhereClauseArgument(whereClauses, dashboardKey) {
    const whereClausesValue = whereClauses ?? {};
    const whereClause = {
        ...whereClausesValue.find(
            (w) => w.propertyName.valueField === dashboardKey
        ),
    };

    if (_.isEmpty(whereClause.values) || !whereClause.predicate) {
        console.log(`WhereClause has no values: ${dashboardKey}`); // eslint-disable-line no-console
        return null;
    }

    return whereClause;
}

function buildDataRowArgument(
    dataRow,
    dashboardKey,
    predicateLabels,
    passAsStringValue = false
) {
    const dataRowValue = dataRow ?? {};
    if (!dataRowValue[dashboardKey]) {
        console.log(`DataRow did not include field: ${dashboardKey}`); // eslint-disable-line no-console
        return null;
    }

    if (passAsStringValue) {
        return {
            predicate: {
                textField: predicateLabels.contains,
                valueField: predicateValues.contains,
            },
            values: [dataRowValue[dashboardKey]],
        };
    }

    return {
        predicate: {
            textField: predicateLabels.isIn,
            valueField: predicateValues.isIn,
        },
        values: [
            {
                textField: dataRowValue[dashboardKey],
                valueField: dataRowValue[dashboardKey],
            },
        ],
    };
}

function buildFixedFilterArgument(
    filterKey,
    predicate,
    values,
    predicateLabels
) {
    if (!predicate) {
        // eslint-disable-next-line no-console
        console.log(`Fixed argument has no predicate: ${filterKey}`);
        return null;
    }

    return {
        predicate: {
            textField: predicateLabels[predicate.valueField],
            valueField: predicateValues[predicate.valueField],
        },
        values: values ?? [],
    };
}

export function buildLinkConfiguration(
    argumentConfigs,
    filterValues,
    timeAndPrecision,
    whereClauses,
    dataRow,
    predicateLabels
) {
    let linkTimeAndPrecision = {};
    const linkFilterValues = {};
    const linkWhereClauses = [];

    /* eslint-disable complexity */
    argumentConfigs.forEach((argumentConfig) => {
        const {
            dashboardKey,
            filterKey,
            passAsStringValue,
            predicate,
            valueOrigin,
            values,
        } = argumentConfig;

        switch (valueOrigin) {
            case 'timeAndPrecision': {
                linkTimeAndPrecision =
                    buildTimeAndPrecisionArgument(timeAndPrecision);
                break;
            }

            case 'filter': {
                const filter = buildFilterValueArgument(
                    filterValues,
                    dashboardKey
                );

                if (filter) {
                    linkFilterValues[filterKey] = filter;
                }
                break;
            }

            case 'where': {
                const whereClause = buildWhereClauseArgument(
                    whereClauses,
                    dashboardKey
                );

                if (whereClause) {
                    linkWhereClauses.push(whereClause);
                }
                break;
            }

            case 'dataRow': {
                const filterFromDataRow = buildDataRowArgument(
                    dataRow,
                    dashboardKey,
                    predicateLabels,
                    passAsStringValue
                );

                if (filterFromDataRow) {
                    linkFilterValues[filterKey] = filterFromDataRow;
                }
                break;
            }

            case 'fixed': {
                const fixedFilter = buildFixedFilterArgument(
                    filterKey,
                    predicate,
                    values,
                    predicateLabels
                );

                if (fixedFilter) {
                    linkFilterValues[filterKey] = fixedFilter;
                }
                break;
            }

            default: {
                break;
            }
        }
    });
    /* eslint-enable complexity */

    return {
        timeAndPrecision: linkTimeAndPrecision,
        filterValues: linkFilterValues,
        whereClauses: linkWhereClauses,
    };
}

function convertConfigurationString(convertedConfig, find, replace) {
    return convertedConfig.split(find).join(replace);
}

export function serializeExternalLinkParameter(configuration) {
    const initialConfigurationString = JSON.stringify(configuration);

    return configKeyMap.reduce(
        (result, keyMap) =>
            convertConfigurationString(
                result,
                keyMap.original,
                keyMap.transpiled
            ),
        initialConfigurationString
    );
}

export function deserializeExternalLinkConfiguration(externalLinkString) {
    const externalLinkConfiguration = configKeyMap.reduce(
        (result, keyMap) =>
            convertConfigurationString(
                result,
                keyMap.transpiled,
                keyMap.original
            ),
        externalLinkString
    );

    return JSON.parse(externalLinkConfiguration);
}

export function decompressEncodedURLToConfiguration(urlParam) {
    return codec
        .decompress(urlParam)
        .then((json) => deserializeExternalLinkConfiguration(json));
}

export function getDashboardLinkParameterForDashboard(configuration = {}) {
    const serializedConfiguration =
        serializeExternalLinkParameter(configuration);

    return codec
        .compress(serializedConfiguration)
        .then((result) => `${DASHBOARD_LINK}=${result}`);
}

export function buildWidgetFilterState(
    defaultTimeAndPrecision,
    defaultFilterValues,
    defaultWhereClauses,
    configuration
) {
    const { id, timeAndPrecision, filterValues, whereClauses } = configuration;

    return {
        id,
        timeAndPrecision: mergeTimeAndPrecision(
            defaultTimeAndPrecision,
            timeAndPrecision
        ),
        filterValues: mergeFilterValues(defaultFilterValues, filterValues),
        whereClauses: mergeWhereClauses(defaultWhereClauses, whereClauses),
    };
}

function getNavigationUrl(isAbsoluteUri, pathName, uriConstants) {
    return isAbsoluteUri ? pathName : uriConstants.getNonApiUri(pathName);
}

export async function buildLinkWithFilters(
    navigationLink,
    widget,
    uriConstants,
    predicateLabels
) {
    const { dataRow, timeAndPrecision, filterValues, whereClauses } = widget;
    const { isAbsoluteUri, pathName, argumentConfigs } = navigationLink;

    const linkConfiguration = buildLinkConfiguration(
        argumentConfigs,
        filterValues,
        timeAndPrecision,
        whereClauses,
        dataRow,
        predicateLabels
    );

    const externalLinkParameter = await getDashboardLinkParameterForDashboard(
        linkConfiguration
    );

    const baseUrl = getNavigationUrl(isAbsoluteUri, pathName, uriConstants);

    return `${baseUrl}?${externalLinkParameter}`;
}

// eslint-disable-next-line complexity
export function shouldNotCreateLink(navigationLink, panelWidgetsFilterState) {
    if (!navigationLink) {
        return true;
    }

    if (navigationLink.argumentConfigs && _.isEmpty(panelWidgetsFilterState)) {
        return true;
    }

    return false;
}

export function shouldCreateStaticLink(navigationLink) {
    return !navigationLink.argumentConfigs;
}

function getWidgetFilters(navigationLink, panelWidgetsFilterState) {
    /* eslint-disable indent */
    const widgetFilters =
        panelWidgetsFilterState.length > 1
            ? panelWidgetsFilterState.find(
                  (x) => x.id === navigationLink.widgetId
              )
            : panelWidgetsFilterState[0];
    /* eslint-enable indent */

    if (!widgetFilters) {
        throw Error('Panel has incorrectly configured widgetId');
    }

    return widgetFilters;
}

// eslint-disable-next-line complexity
export async function getNavigationLink(
    navigationLink,
    uriConstants,
    widgetsFilterState,
    predicateLabels
) {
    if (shouldNotCreateLink(navigationLink, widgetsFilterState)) {
        return null;
    }

    if (shouldCreateStaticLink(navigationLink)) {
        return navigationLink.isAbsoluteUri
            ? navigationLink.pathName
            : uriConstants.getNonApiUri(navigationLink.pathName);
    }

    const widgetFilters = getWidgetFilters(navigationLink, widgetsFilterState);

    return buildLinkWithFilters(
        navigationLink,
        widgetFilters,
        uriConstants,
        predicateLabels
    );
}

export function getShareInfo(viewKey, dashboardKey, getShareableUri) {
    const id = uuid();

    return {
        id,
        link: getShareableUri(
            `Dashboard/${dashboardKey}/${viewKey}?${PERSISTED_LINK}=${id}`
        ),
    };
}

export function getPrintShareInfo(
    viewKey,
    dashboardKey,
    getShareableUri,
    orientation = printOrientation.portrait
) {
    const id = uuid();

    return {
        id,
        link: getShareableUri(
            `Dashboard/Print/${orientation}/${dashboardKey}/${viewKey}?${PERSISTED_LINK}=${id}`
        ),
    };
}

export function shareDashboardLink(id, configurationForShare, saveSharedUrl) {
    const serializedConfig = serializeExternalLinkParameter(
        configurationForShare
    );

    return codec.compress(serializedConfig).then((payload) =>
        putDataPromise(saveSharedUrl, {
            id,
            payload,
        })
    );
}
